/**
 * Die Standardwerte dieses Projekts. Eingecheckt, mit Absicht.
 *
 * Hier stehen ausschließlich Adressen und eine Client-ID. Nichts davon
 * ist geheim: Alles, was das Frontend zum Anmelden und Abrufen braucht,
 * landet ohnehin im Browser-Bundle und ist für jeden lesbar, der die
 * Seite öffnet. Die Anmeldung ist genau darauf ausgelegt (PKCE, kein
 * Client-Secret).
 *
 * Der Sinn: Wer das Repo klont, kann `bun install` und `bun run dev`
 * tippen und ist fertig. Keine Datei kopieren, niemanden nach Werten
 * fragen.
 *
 * Abweichen geht über eine `.env`, die überschreibt jeden Wert hier und
 * bleibt aus dem Repo draußen. Siehe `.env.example`.
 *
 * **Hier gehört kein Geheimnis hinein.** Kein Client-Secret, kein
 * Passwort, kein API-Key. Wer so etwas braucht, nimmt die `.env`, und
 * selbst dort gilt: Was mit VITE_ anfängt, ist öffentlich.
 */
export const appDefaults = {
  /**
   * Basis-URL des Backends für Staging und Entwicklung.
   *
   * Das ist der Standard für `vite dev` und `vite build --mode staging`.
   * Die Produktions-URL kommt nur bei `vite build` (default mode
   * `production`) zum Einsatz. Siehe env.ts für die genaue Logik.
   */
  apiBaseUrl: 'https://staging.backend.panda-weltmaschine.de',

  /**
   * Basis-URL des Backends für den Produktionsbetrieb.
   *
   * Wird nur verwendet, wenn Vite im `production`-Mode baut, also bei
   * `vite build` ohne eigenen Mode. Ein explizites
   * `VITE_API_BASE_URL` in der .env überschreibt beide.
   */
  productionApiBaseUrl: 'https://backend.panda-weltmaschine.de',

  /**
   * Basis-URL von PostgREST, der REST-Schicht direkt über der Datenbank.
   *
   * Zweite Datenquelle neben der Backend-API, ausschließlich lesend. Sie
   * erspart einen eigenen Endpunkt in der Weltmaschine für jede einzelne
   * Kennzahl. Wann welche Quelle richtig ist, steht in
   * `docs/data-sources.md`.
   *
   * Nur ein Wert, keine Staging-Variante: PostgREST gibt es genau einmal
   * und es zeigt auf die Produktionsdatenbank. Wer die App gegen das
   * Staging-Backend laufen lässt, liest über PostgREST trotzdem echte
   * Produktionsdaten. Das ist unkritisch, weil der Zugang nur lesen darf,
   * sollte einem beim Vergleich von Zahlen aber bewusst sein.
   */
  postgrestBaseUrl: 'https://db.panda-weltmaschine.de',

  /**
   * Leitet Anfragen im Entwicklungsmodus über den Vite-Server.
   *
   * Steht bewusst auf true: Die Weltmaschine gibt per CORS nur ihre
   * eigenen Frontends frei, und localhost:5173 steht dort nicht drin.
   * Ohne den Proxy scheitert jeder Aufruf am Preflight. Gilt für beide
   * Ziele, Backend-API wie PostgREST.
   */
  useDevProxy: true,

  /** Realm-URL des Identity Providers. */
  oidcAuthority: 'https://auth.panda-weltmaschine.de/realms/panda',

  /**
   * Client-ID in Keycloak. Eigener Client, nicht der geteilte `weltmaschine`.
   *
   * Der Grund liegt bei PostgREST: Es liest die Datenbankrolle aus dem
   * Claim `db_role`, und der kommt aus einem Client Scope. Der Client
   * `weltmaschine` trägt den Scope `db-scope` mit dem Wert
   * `panda-db-user`, also dem Eigentümer der Schemas mit vollen
   * Schreibrechten. Dieses Frontend soll nur lesen dürfen und bekommt
   * deshalb über den Scope `insights` den Wert `insights_readonly`.
   *
   * Ein Scope trägt nur einen Wert, zwei Rollen an einem Client gehen
   * also nicht. Daher der eigene Client.
   */
  oidcClientId: 'weltmaschine-insights',

  /**
   * Angeforderte Scopes.
   *
   * `roles` ist nicht optional: Die Weltmaschine autorisiert über
   * Keycloak-Realm-Rollen (`all:write`), nicht über OAuth-Scopes.
   */
  oidcScope: 'openid profile email roles',
} as const

/**
 * Pfad-Präfix, unter dem der Dev-Server PostgREST durchreicht.
 *
 * Steht hier und nicht in `src/config/env.ts`, weil `vite.config.ts` den
 * Wert ebenfalls braucht und diese Datei nicht importieren kann: `env.ts`
 * wertet beim Import `import.meta.env` aus, das es im Node-Kontext der
 * Vite-Konfiguration nicht gibt.
 *
 * Warum überhaupt ein Präfix: Backend-Routen tragen ihr `/api` selbst,
 * PostgREST-Pfade sind dagegen nackte Tabellennamen wie `/products`. Ohne
 * eigenes Präfix wären sie vom Rest der Anwendung nicht zu unterscheiden.
 * Der Vite-Proxy schneidet es beim Weiterleiten wieder ab.
 */
export const POSTGREST_PROXY_PREFIX = '/db'
